/**
 * A labelled group inside a page: the uppercase, tracked, faint label with its
 * content beneath.
 *
 * Carried over from `namu-app`'s `Section`, which uses the same treatment on
 * its settings and profile screens, so a staff member who also uses the phone
 * app meets the same shape in both. The label style is `--type-label` with
 * `--tracking-label`, and that pairing exists in `tokens.css` precisely so it
 * is not reinvented per screen at a slightly different tracking.
 *
 * The label is a real heading rather than a styled paragraph. It is the thing a
 * screen reader user navigates a long settings page by, and a page of eleven
 * groups with no headings is a page that has to be read from the top every
 * time.
 */

import { useId } from 'react'
import type { HTMLAttributes, ReactNode } from 'react'
import styles from './Section.module.css'

export interface SectionProps extends HTMLAttributes<HTMLElement> {
  /** The uppercase group label. Omit for an unlabelled run of content. */
  label?: string
  description?: ReactNode
  /** Controls for the group, on the label's line. */
  actions?: ReactNode
  /**
   * The heading level. `h2` under a PageHeader's `h1`; `h3` inside a Panel that
   * already has one. Levels that skip are what turn a heading outline into a
   * list, and the outline is the whole point of having them.
   */
  headingLevel?: 2 | 3 | 4
  children: ReactNode
}

export function Section({
  label,
  description,
  actions,
  headingLevel = 2,
  className,
  children,
  ...rest
}: SectionProps) {
  const labelId = useId()
  const Heading = `h${headingLevel}` as const
  const sectionClassName = [styles.section, className].filter(Boolean).join(' ')

  return (
    <section
      className={sectionClassName}
      {...(label ? { 'aria-labelledby': labelId } : {})}
      {...rest}
    >
      {label || actions ? (
        <div className={styles.head}>
          <div className={styles.headings}>
            {label ? (
              <Heading className={styles.label} id={labelId}>
                {label}
              </Heading>
            ) : null}
            {description ? <p className={styles.description}>{description}</p> : null}
          </div>
          {actions ? <div className={styles.actions}>{actions}</div> : null}
        </div>
      ) : null}

      <div className={styles.body}>{children}</div>
    </section>
  )
}
